import { useContext, useEffect, useState } from 'react';
import { SmartContext } from '../../../library/Core/SmartContext';
import { axiosClient } from '../../../library/Service/axiosClient';
import { getDomainValueForCode, getTimeAgo } from '../../../library/Core/SmartFunctions';

const STATUS_MAP: any = {
    '': null, // All Status
    IMMEDIATELY: 'IMMEDIATELY',
    EXPLORING: 'EXPLORING',
    INTERESTED: 'INTERESTED',
};

const DEFAULT_MESSAGES: any = {
    IMMEDIATELY:
        "We noticed you're ready to start right away. We'd love to move forward quickly—expect to hear from us soon about next steps.",

    EXPLORING:
        "Thanks for your interest! We'd like to learn more about what you're looking for. Stay tuned for updates on this opportunity.",

    INTERESTED: "Thanks for keeping us on your radar. We'll reach out when timing aligns better or new opportunities arise.",
};

const SendMessageToInterested = ({ handleOpenModal }: any) => {
    const { state, dispatch } = useContext(SmartContext);

    const showFlag = state?.modalFlags?.showSendMessageToInterestedModal;
    const jobData = state?.modalData?.data;

    const [interestedList, setInterestedList] = useState<any[]>([]);
    const [recipientType, setRecipientType] = useState<'ALL' | 'ALUMNI' | 'STUDENT'>('ALL');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    // Fetch users when modal opens
    useEffect(() => {
        if (!showFlag || !jobData?.id) return;

        const fetchInterestedUsers = async () => {
            try {
                const response = await axiosClient().post(`${process.env.REACT_APP_JOB_SERVER}/jobs/intrested-applied-users`, {
                    jobId: jobData.id,
                    type: 'INTERESTED',
                });

                if (response.data?.success) {
                    setInterestedList(response.data.data);
                }
            } catch (err) {
                console.error(err);
                setInterestedList([]);
            }
        };

        fetchInterestedUsers();
    }, [showFlag, jobData?.id]);

    useEffect(() => {
        if (statusFilter && DEFAULT_MESSAGES[statusFilter]) {
            setMessage(DEFAULT_MESSAGES[statusFilter]);
        }

        // set default message if text area is empty
        if (message?.trim() === '') {
            setMessage(DEFAULT_MESSAGES[statusFilter]);
        }
    }, [statusFilter]);

    console.log('Interested List:', interestedList);

    // Counts
    const alumniCount = interestedList.filter((u) => u.userTypeCode === 'ALUMNI').length;
    const studentCount = interestedList.filter((u) => u.userTypeCode === 'STUDENT').length;

    // Filtering logic
    const applyFilters = () => {
        let list = [...interestedList];

        // Filter by recipient type
        if (recipientType === 'ALUMNI') list = list.filter((u) => u.userTypeCode === 'ALUMNI');
        if (recipientType === 'STUDENT') list = list.filter((u) => u.userTypeCode === 'STUDENT');

        // 2. Filter by status
        const statusValue = STATUS_MAP[statusFilter];
        if (statusValue !== null) list = list.filter((u) => u.isInterested === statusValue);

        return list;
    };

    const finalList = applyFilters();
    const recipientCount = finalList.length;

    const charCount = (message || '').length;

    const overLimit = charCount > 1000;

    const safeMessage = message || '';

    const canSend = safeMessage.trim().length > 0 && !overLimit && recipientCount > 0 && !isSending;

    // Send Button Handler
    const handleSendMessages = async (e: any) => {
        e.preventDefault();
        if (!canSend) return;

        if (!window.confirm(`Send message to ${recipientCount} candidates?`)) return;

        try {
            setIsSending(true);

            const payload = {
                jobId: jobData.id,
                recipients: finalList.map((u) => ({
                    email: u.userEmail,
                    uuid: u.uuid,
                })),
                message,
            };

            const response = await axiosClient().post(`${process.env.REACT_APP_JOB_SERVER}/jobs/send-bulk-message`, payload);

            if (response.data?.success) {
                // alert('Messages sent successfully!');

                dispatch({
                    type: 'TOGGLE_MODAL_VISIBILITY',
                    payload: { modalName: 'showSendMessageToInterestedModal' },
                });
            }
        } catch (err) {
            //console.error(err);
            alert('Failed to send message.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <>
            <div className={`modal fade px-3 ${showFlag ? 'show d-block' : ''}`} tabIndex={-1}>
                <div className="modal-dialog modal-dialog-centered max-700 mx-auto">
                    <div className="modal-content radius-15">
                        <div className="modal-body p-4 p-md-5">
                            <h5 className="font-32 font-600 mb-2">Send Bulk Message</h5>
                            <hr className="opacity-25" />

                            <button
                                className="position-absolute top-20 right-20 border-0 bg-transparent font-30"
                                onClick={() =>
                                    dispatch({
                                        type: 'TOGGLE_MODAL_VISIBILITY',
                                        payload: { modalName: 'showSendMessageToInterestedModal' },
                                    })
                                }>
                                <i className="bi bi-x-circle-fill"></i>
                            </button>

                            {/* Recipient Selection */}
                            <label className="font-16 font-500 mb-1">Select Recipients</label>
                            <div className="d-flex gap-3 mb-3">
                                <label className="form-check">
                                    <input
                                        type="radio"
                                        className="form-check-input"
                                        checked={recipientType === 'ALL'}
                                        onChange={() => setRecipientType('ALL')}
                                    />
                                    All Candidates ({interestedList.length})
                                </label>

                                {alumniCount > 0 && (
                                    <label className="form-check">
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            checked={recipientType === 'ALUMNI'}
                                            onChange={() => setRecipientType('ALUMNI')}
                                        />
                                        Alumni ({alumniCount})
                                    </label>
                                )}

                                {studentCount > 0 && (
                                    <label className="form-check">
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            checked={recipientType === 'STUDENT'}
                                            onChange={() => setRecipientType('STUDENT')}
                                        />
                                        Students ({studentCount})
                                    </label>
                                )}
                            </div>

                            {/* Status Filter */}
                            <label className="font-16 font-500 mb-1">Filter by Status</label>
                            <select
                                id="statusFilter"
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="">All Status</option>
                                <option value="IMMEDIATELY">Available Immediately</option>
                                <option value="EXPLORING">Exploring Opportunities</option>
                                <option value="INTERESTED">Interested for Future</option>
                            </select>

                            {/* Message Textarea */}
                            <label className="font-16 font-500 mb-1">Message*</label>
                            <textarea
                                className="form-control h-auto"
                                rows={3}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Type your message..."
                            />

                            <div className={`text-end mt-1 ${overLimit ? 'text-danger' : ''}`}>{charCount}/1000</div>

                            {/* Recipient Count */}
                            <div className="bg-light p-3 rounded mt-3">
                                <strong>Recipients:</strong> {recipientCount}
                                <div>
                                    <i>Messages will be sent via email & in-app notifications</i>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="text-center mt-4">
                                {/* <button className="btn btn-border-gray me-3" disabled={isSending}>
                                    Cancel
                                </button> */}

                                <button className="btn btn-green" disabled={!canSend} onClick={handleSendMessages}>
                                    {isSending ? 'Sending...' : 'Send Messages'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SendMessageToInterested;
